/**
 * Spec 109 §6 — the diagnosis deliverable dashboard (the consulting/pitch
 * centerpiece). Renders the full outcome -> cause -> fix arc:
 *
 *   - Combined confidence + limiting factor, PROMINENT at the top (§5 honesty).
 *   - Outcome (observational): observed visibility + sample size + CI.
 *   - Cause: each implicated page with page persona-fit (107) AND GEO rules (083)
 *     SIDE BY SIDE (never blended), the diagnosis_type in plain language, and the
 *     narrative with verbatim quotes highlighted.
 *   - Fix: persona-grounded recommendations, each with the driving persona
 *     attribute (persona_grounding) + the distinct_from_geo contrast + before/after.
 *
 * The two lens scores are NEVER averaged into one number — that distinction is
 * the entire value of the diagnosis.
 */
import type {
  ImplicatedPage,
  PersonaDiagnosisFull,
  PersonaDiagnosisRecommendation,
  QuoteUsed,
} from '../api/personaDiagnoses.js';
import {
  DIAGNOSIS_TYPE_LABEL,
  LINKAGE_SIGNAL_LABEL,
  RECOMMENDATION_KIND_LABEL,
  RECOMMENDATION_PRIORITY_LABEL,
} from '../api/personaDiagnoses.js';

function fmtScore(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

function fmtPct(n: number | null): string {
  return n == null ? '—' : `${Math.round(n * 100)}%`;
}

// Four confidence tiers onto three status tokens: `low` and `very_low` both
// read as danger (below-acceptable confidence), `moderate` as warning.
const CONFIDENCE_COLOR: Record<string, string> = {
  high: 'var(--hub-color-success)',
  moderate: 'var(--hub-color-warning)',
  low: 'var(--hub-color-danger)',
  very_low: 'var(--hub-color-danger)',
};

// `critical` and `high` both read as danger (only two "elevated" status
// tokens exist for four priority tiers); `low` was never a status color in
// the original palette (a muted grey, not a hue), so it stays muted ink
// rather than being forced onto a status token.
const PRIORITY_COLOR: Record<string, string> = {
  critical: 'var(--hub-color-danger)',
  high: 'var(--hub-color-danger)',
  medium: 'var(--hub-color-warning)',
  low: 'var(--hub-color-ink-muted)',
};

/* ------------------------------------------------------------------ */
/* Narrative with verbatim quotes highlighted                         */
/* ------------------------------------------------------------------ */

function HighlightedNarrative({
  narrative,
  quotes,
}: {
  narrative: string;
  quotes: QuoteUsed[];
}) {
  // Build a regex of the verbatim quotes (longest first) and wrap matches in <mark>.
  const ordered = [...quotes]
    .map((q) => q.quote)
    .filter((q) => q && q.length >= 4)
    .sort((a, b) => b.length - a.length);

  if (ordered.length === 0) {
    return (
      <p data-testid="diagnosis-narrative" style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
        {narrative}
      </p>
    );
  }

  const escaped = ordered.map((q) => q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = narrative.split(re);

  return (
    <p data-testid="diagnosis-narrative" style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
      {parts.map((part, i) => {
        const isQuote = ordered.some((q) => q.toLowerCase() === part.toLowerCase());
        return isQuote ? (
          <mark
            key={i}
            data-testid="diagnosis-narrative-quote"
            style={{ background: 'var(--hub-color-warning-soft)', padding: '0 2px', borderRadius: 2 }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Cause — implicated page with both scores side by side              */
/* ------------------------------------------------------------------ */

function CausePageRow({ page }: { page: ImplicatedPage }) {
  const isAbsence = page.linkage_signal === 'absence_inference';
  return (
    <div
      data-testid="diagnosis-cause-page"
      data-linkage-signal={page.linkage_signal}
      style={{ border: '1px solid var(--hub-color-line)', borderRadius: 6, padding: 12, marginBottom: 8 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--hub-color-ink-muted)', wordBreak: 'break-all' }}>
          {page.page_url || '(no page positioned — content gap)'}
        </span>
        <span
          style={{
            background: 'var(--hub-color-info-soft)',
            color: 'var(--hub-color-action-text)',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {LINKAGE_SIGNAL_LABEL[page.linkage_signal]}
        </span>
      </div>

      {!isAbsence && (
        <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
          <div data-testid="diagnosis-page-geo-score">
            <div style={{ fontSize: 12, color: 'var(--hub-color-ink-muted)' }}>
              GEO rules
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{fmtScore(page.geo_score)}</div>
          </div>
          <div aria-hidden style={{ borderLeft: '1px solid var(--hub-color-line)', alignSelf: 'stretch' }} />
          <div data-testid="diagnosis-page-personafit-score">
            <div style={{ fontSize: 12, color: 'var(--hub-color-ink-muted)' }}>
              Page persona-fit
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{fmtScore(page.persona_fit_score)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fix — persona-grounded recommendation                              */
/* ------------------------------------------------------------------ */

function FixRow({ rec }: { rec: PersonaDiagnosisRecommendation }) {
  return (
    <div
      data-testid="diagnosis-fix-rec"
      data-rec-kind={rec.recommendation_kind}
      style={{ border: '1px solid var(--hub-color-line)', borderRadius: 6, padding: 12, marginBottom: 8 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span
          data-testid="rec-kind"
          style={{ fontWeight: 600, fontSize: 14 }}
        >
          {RECOMMENDATION_KIND_LABEL[rec.recommendation_kind]}
        </span>
        <span
          style={{
            color: PRIORITY_COLOR[rec.priority] ?? 'var(--hub-color-ink-muted)',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {RECOMMENDATION_PRIORITY_LABEL[rec.priority]}
        </span>
      </div>

      {rec.target_page_url && (
        <div style={{ fontSize: 12, color: 'var(--hub-color-ink-muted)', wordBreak: 'break-all', marginTop: 4 }}>
          {rec.target_page_url}
        </div>
      )}

      <div
        data-testid="rec-persona-grounding"
        style={{ marginTop: 8, fontSize: 13, color: 'var(--hub-color-ink)' }}
      >
        <strong style={{ color: 'var(--hub-color-action-text)' }}>Driving persona attribute:</strong>{' '}
        {rec.persona_grounding}
      </div>

      <div
        data-testid="rec-distinct-from-geo"
        style={{
          marginTop: 8,
          fontSize: 13,
          color: 'var(--hub-color-ink)',
          background: 'var(--hub-color-surface-sunken)',
          borderLeft: '3px solid var(--hub-color-action)',
          padding: '6px 10px',
          borderRadius: 4,
        }}
      >
        <strong>Distinct from GEO:</strong> {rec.distinct_from_geo}
      </div>

      {(rec.specific_change.before_quote || rec.specific_change.suggested_after) && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          {rec.specific_change.before_quote && (
            <div data-testid="rec-before-quote" style={{ color: 'var(--hub-color-danger)' }}>
              <strong>Before:</strong> &ldquo;{rec.specific_change.before_quote}&rdquo;
            </div>
          )}
          {rec.specific_change.suggested_after && (
            <div data-testid="rec-suggested-after" style={{ color: 'var(--hub-color-success)', marginTop: 2 }}>
              <strong>After:</strong> {rec.specific_change.suggested_after}
            </div>
          )}
          {rec.specific_change.rationale && (
            <div style={{ color: 'var(--hub-color-ink-muted)', marginTop: 2 }}>{rec.specific_change.rationale}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The deliverable                                                    */
/* ------------------------------------------------------------------ */

export function PersonaDiagnosisDeliverable({
  diagnosis,
}: {
  diagnosis: PersonaDiagnosisFull;
}) {
  const typeLabel = diagnosis.diagnosis_type
    ? DIAGNOSIS_TYPE_LABEL[diagnosis.diagnosis_type]
    : diagnosis.status === 'completed'
      ? 'Healthy: adequately visible, no diagnosis needed'
      : `Status: ${diagnosis.status}`;

  const strength = (diagnosis.response_evidence_strength ?? {}) as Record<string, unknown>;
  const sampleSize =
    typeof strength['sample_size'] === 'number'
      ? (strength['sample_size'] as number)
      : typeof strength['n'] === 'number'
        ? (strength['n'] as number)
        : null;
  const ciLower = typeof strength['ci_lower'] === 'number' ? (strength['ci_lower'] as number) : null;
  const ciUpper = typeof strength['ci_upper'] === 'number' ? (strength['ci_upper'] as number) : null;

  const isContentGap = diagnosis.diagnosis_type === 'content_gap';

  // A completed, healthy diagnosis (no diagnosis needed) has no confidence cap
  // because nothing was wrong — show "N/A — healthy" rather than implying the
  // job is still running. "not yet determined" is reserved for genuinely
  // non-terminal/pending diagnoses.
  const isHealthyTerminal =
    diagnosis.status === 'completed' && diagnosis.diagnosis_type == null;
  const limitingFactor =
    diagnosis.confidence_limiting_factor ??
    (isHealthyTerminal ? 'N/A — healthy' : 'not yet determined');

  return (
    <div
      className="persona-diagnosis-deliverable"
      data-testid="persona-diagnosis-deliverable"
      style={{ border: '1px solid var(--hub-color-line-strong)', borderRadius: 8, padding: 16, maxWidth: 820 }}
    >
      {/* Combined confidence + limiting factor — PROMINENT at the top (§5). */}
      <div
        style={{
          background: 'var(--hub-color-surface-sunken)',
          border: '1px solid var(--hub-color-line-strong)',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div
          data-testid="diagnosis-combined-confidence"
          data-confidence={diagnosis.combined_confidence ?? ''}
          style={{
            fontWeight: 800,
            fontSize: 18,
            color: CONFIDENCE_COLOR[diagnosis.combined_confidence ?? ''] ?? 'var(--hub-color-ink-muted)',
          }}
        >
          Combined confidence: {diagnosis.combined_confidence ?? '—'}
        </div>
        <div data-testid="diagnosis-limiting-factor" style={{ fontSize: 13, color: 'var(--hub-color-ink-muted)' }}>
          Limiting factor: {limitingFactor}
        </div>
      </div>

      {/* diagnosis_type callout */}
      <div
        data-testid="diagnosis-type-label"
        data-diagnosis-type={diagnosis.diagnosis_type ?? ''}
        style={{
          background:
            diagnosis.diagnosis_type === 'audience_mismatch'
              ? 'var(--hub-color-danger-soft)'
              : 'var(--hub-color-surface-sunken)',
          border: '1px solid var(--hub-color-line-strong)',
          borderRadius: 6,
          padding: '10px 12px',
          fontWeight: 600,
          fontSize: 15,
          marginBottom: 16,
        }}
      >
        {typeLabel}
      </div>

      {/* ── OUTCOME (observational) ── */}
      <section style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 13, color: 'var(--hub-color-action-text)', margin: '0 0 6px' }}>
          Outcome <span style={{ color: 'var(--hub-color-ink-muted)', fontWeight: 400 }}>(observational)</span>
        </h3>
        <div data-testid="diagnosis-outcome" style={{ fontSize: 14 }}>
          For the {diagnosis.persona_type} persona, the brand appears in{' '}
          <strong>{fmtPct(diagnosis.observed_visibility)}</strong> of sampled responses on this cluster.
        </div>
        <div data-testid="diagnosis-outcome-ci" style={{ fontSize: 12, color: 'var(--hub-color-ink-muted)', marginTop: 2 }}>
          Sample size: {sampleSize ?? '—'}
          {ciLower != null && ciUpper != null
            ? ` · 95% CI [${fmtPct(ciLower)}, ${fmtPct(ciUpper)}]`
            : ' · CI unavailable'}
        </div>
      </section>

      {/* ── CAUSE ── */}
      <section style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 13, color: 'var(--hub-color-action-text)', margin: '0 0 6px' }}>
          Cause
        </h3>
        {diagnosis.implicated_pages.length === 0 ? (
          <p style={{ color: 'var(--hub-color-ink-subtle)', fontSize: 13 }}>No implicated pages recorded.</p>
        ) : (
          diagnosis.implicated_pages.map((p, i) => <CausePageRow key={`${p.page_url}-${i}`} page={p} />)
        )}

        {isContentGap && diagnosis.coverage_gap_context_id && (
          <p style={{ marginTop: 4 }}>
            <a
              data-testid="content-gap-link"
              href={`/coverage?gap=${diagnosis.coverage_gap_context_id}`}
              style={{ color: 'var(--hub-color-action-text)', fontWeight: 600 }}
            >
              View the coverage-gap recommendation for this cluster →
            </a>
          </p>
        )}

        {diagnosis.diagnosis_narrative && (
          <div style={{ marginTop: 8 }}>
            <HighlightedNarrative
              narrative={diagnosis.diagnosis_narrative}
              quotes={diagnosis.quotes_used ?? []}
            />
          </div>
        )}
      </section>

      {/* ── FIX ── */}
      <section>
        <h3 style={{ fontSize: 13, color: 'var(--hub-color-action-text)', margin: '0 0 6px' }}>
          Fix
        </h3>
        {diagnosis.recommendations.length === 0 ? (
          <p style={{ color: 'var(--hub-color-ink-subtle)', fontSize: 13 }}>No recommendations generated yet.</p>
        ) : (
          diagnosis.recommendations.map((rec) => <FixRow key={rec.id} rec={rec} />)
        )}
      </section>
    </div>
  );
}
